import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import styles from './Feedback.module.css'

export interface ToastAction {
  readonly label: string
  readonly onAction: () => void
}

export interface ToastOptions {
  readonly message: string
  readonly action?: ToastAction
  readonly tone?: 'neutral' | 'danger'
  /** Milliseconds before auto-dismissal. */
  readonly durationMs?: number
}

interface ActiveToast extends ToastOptions {
  readonly id: number
}

interface ToastApi {
  readonly show: (options: ToastOptions) => void
}

const ToastContext = createContext<ToastApi | null>(null)

/**
 * Toasts carrying an undo stay up longer.
 *
 * The default is a notification; the longer one is a decision. Four seconds is
 * not enough to notice a message, read it, and decide to reverse something —
 * and an undo that expires before it is seen is the same as no undo.
 *
 * The prototype specifies 4.2s flat. That is right for a confirmation and wrong
 * for an undo, and T-804's own first acceptance criterion — "survives long
 * enough to be acted on" — is the one that decides it.
 */
const DEFAULT_MS = 4200
const WITH_ACTION_MS = 9000

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  /*
   * One at a time, replacing rather than stacking.
   *
   * The consequence worth being explicit about is that **a replaced action is
   * no longer undoable**. Keeping a hidden undo alive after its toast has gone
   * means a user can delete two entries, press undo once, and get back the
   * wrong one. Tying undoability to the visible affordance keeps the model
   * honest: if you cannot see it, it is gone.
   */
  const [toast, setToast] = useState<ActiveToast | null>(null)
  const nextId = useRef(0)

  /*
   * Dismissal is by id, so an expiring timer can only ever clear the toast it
   * was started for. Without that check a short confirmation raised before a
   * long undo would take the undo down with it when its own timer came round —
   * cutting a nine-second decision to whatever was left of four.
   */
  const dismiss = useCallback((id: number) => {
    setToast((current) => (current?.id === id ? null : current))
  }, [])

  const show = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++
      setToast({ ...options, id })
      const duration =
        options.durationMs ?? (options.action ? WITH_ACTION_MS : DEFAULT_MS)
      setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  const api = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        A permanently mounted live region, not one that appears with the first
        toast. Screen readers only announce changes inside a region that already
        existed when the change happened — a region added at the same moment as
        its content is silent.

        It carries no styling: the toast positions itself, because the animation
        that centres it has to run on the centred element.
      */}
      <div role="status" aria-live="polite">
        {toast && (
          // Keyed by id so a replacement remounts rather than swapping text in
          // place, which would leave the arrival animation already finished.
          <div
            key={toast.id}
            className={`${styles['toast']} ${toast.tone === 'danger' ? styles['toastDanger'] : ''} toast-in`}
          >
            <span className={styles['toastMessage']}>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className={styles['toastAction']}
                onClick={() => {
                  toast.action?.onAction()
                  dismiss(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) {
    throw new Error('useToast must be used inside a <ToastProvider>')
  }
  return api
}
